import { ScopeContext, useScope } from 'dreamstate';
import { default as throttle } from 'lodash/throttle';
import {
  MutableRefObject,
  RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
} from 'react';

import { Optional, Maybe, Id } from '@vimeo/core/types/generic';
import { ELiveInteractionQuery } from '@vimeo/live-core/store/queries';
import {
  EQuestionSortType,
  IQnAQuestion,
  IQnASortingRule,
} from '@vimeo/live-core/types/interaction_tools';
import { scrollElementIntoView } from '@vimeo/live-core/utils/dom/scroll';
import { inline } from '@vimeo/live-core/utils/general/lambda';
import { getAbsoluteNow } from '@vimeo/live-core/utils/general/time';

/**
 * Handle showing notifications about new questions in list of QNA.
 * Based on mount render + last time when newest item was seen.
 *
 * - Show new questions notification if it is not disabled
 * - When do not see latest message, try to display notification when it appears
 * - When load widget, consider point of load as `seen it all`
 * - Do not treat own messages (with self ID) as new ones (does not work with anonymous)
 */
export function useQuestionListNotification(
  listRef: RefObject<HTMLDivElement>,
  questions: Array<IQnAQuestion>,
  sorting: IQnASortingRule,
  selectedQuestionElement?: Optional<HTMLDivElement>,
) {
  const scope: ScopeContext = useScope();
  const lastSeenAtRef: MutableRefObject<number> = useRef(getAbsoluteNow());
  const [hasNotSeenMessages, setHasNotSeenMessages] = useState(false);
  const [isSelectedQuestionScrolledAway, setSelectedQuestionScrolledAway] = useState(false);
  const [scrolledAwayNotificationPosition, setScrolledAwayNotificationPosition] =
    useState<Optional<string>>(null);

  const canShowScrolledAway: boolean = Boolean(
    isSelectedQuestionScrolledAway && scrolledAwayNotificationPosition,
  );

  const canShowNewQuestionsNotification: boolean =
    !canShowScrolledAway &&
    hasNotSeenMessages &&
    (sorting.type === EQuestionSortType.NEWEST || sorting.type === EQuestionSortType.OLDEST);
  const newQuestionNotificationPosition: string =
    sorting.type === EQuestionSortType.OLDEST ? 'bottom' : 'top';

  const isLastElementVisible = useCallback(() => {
    const list: Optional<HTMLDivElement> = listRef.current;

    if (!list || !list.children.length) {
      return true;
    }

    const scroll: HTMLDivElement = list.parentElement as HTMLDivElement;
    const mostRecentElement: HTMLElement = (
      sorting.type === EQuestionSortType.OLDEST
        ? list.children[list.childElementCount - 1]
        : list.children[0]
    ) as HTMLElement;

    // Allow element to be partially visible by ~75px to consider it in view.
    const allowedDelta: number = mostRecentElement.clientHeight;

    let viewTop: number = scroll.scrollTop;
    let viewBottom: number = viewTop + scroll.clientHeight;
    let elementTop: number = mostRecentElement.offsetTop;
    let elementBottom: number = elementTop + mostRecentElement.clientHeight;

    return elementBottom - viewBottom < allowedDelta && viewTop - elementTop < allowedDelta;
  }, [listRef, sorting.type]);

  const isSelectedElementVisible = useCallback((): [boolean, Optional<string>] => {
    const list: Optional<HTMLElement> = listRef.current;

    if (!list || !selectedQuestionElement) {
      return [true, null];
    }

    const scroll: HTMLElement = list.parentElement as HTMLElement;
    const element: HTMLElement = selectedQuestionElement.parentElement as HTMLElement;

    // Allow element to be partially visible.
    const allowedDelta: number = element.clientHeight;

    let viewTop: number = scroll.scrollTop;
    let viewBottom: number = viewTop + scroll.clientHeight;
    let elementTop: number = element.offsetTop;
    let elementBottom: number = elementTop + element.clientHeight;

    const isBelow: boolean = elementBottom - viewBottom > allowedDelta;
    const isAbove: boolean = viewTop - elementTop > allowedDelta;
    const isInView: boolean = !isAbove && !isBelow;

    return [
      isInView,
      inline((): Optional<string> => {
        if (isInView) {
          return null;
        }

        if (isBelow) {
          return 'bottom';
        } else if (isAbove) {
          return 'top';
        }

        return null;
      }),
    ];
  }, [listRef, selectedQuestionElement]);

  const onDismissNewQuestionsNotification = useCallback(() => {
    lastSeenAtRef.current = getAbsoluteNow();
    setHasNotSeenMessages(false);
  }, []);

  const onConfirmShowNewQuestions = useCallback(() => {
    lastSeenAtRef.current = getAbsoluteNow();
    setHasNotSeenMessages(false);

    const list: Optional<HTMLDivElement> = listRef.current
      ?.parentElement as Optional<HTMLDivElement>;

    if (list) {
      list.scrollTo({
        top: sorting.type === EQuestionSortType.OLDEST ? list.scrollHeight : 0,
        behavior: 'smooth',
      });
    }
  }, [listRef, sorting.type]);

  const onConfirmScrollToSelectedQuestion = useCallback(() => {
    const list: Optional<HTMLDivElement> = listRef.current
      ?.parentElement as Optional<HTMLDivElement>;

    const [, scrolledAwayPosition] = isSelectedElementVisible();

    if (list && selectedQuestionElement) {
      scrollElementIntoView(selectedQuestionElement.parentElement as HTMLElement, {
        behavior: 'smooth',
        block:
          scrolledAwayPosition === 'top'
            ? 'start'
            : scrolledAwayPosition === 'bottom'
            ? 'end'
            : 'center',
      });
    }
  }, [isSelectedElementVisible, listRef, selectedQuestionElement]);

  const onScrollInQuestionList = useMemo(
    () =>
      throttle(() => {
        if (isLastElementVisible()) {
          lastSeenAtRef.current = getAbsoluteNow();
          setHasNotSeenMessages(false);
        }

        const [isVisible, scrolledAwayPosition] = isSelectedElementVisible();

        setSelectedQuestionScrolledAway(!isVisible);
        setScrolledAwayNotificationPosition(scrolledAwayPosition);
      }, 100),
    [isLastElementVisible, isSelectedElementVisible],
  );

  // When questions list or question sorting changes, recalculate.
  useLayoutEffect(() => {
    if (isLastElementVisible()) {
      lastSeenAtRef.current = getAbsoluteNow();
      setHasNotSeenMessages(false);
    } else {
      const userId: Maybe<Id> = scope.queryDataSync({
        type: ELiveInteractionQuery.LIVE_INTERACTION_USER,
      })?.data?.id;

      // Do not display own messages as `new one`, expect other users.
      setHasNotSeenMessages(
        questions.some(
          (it) => it.createdAt > lastSeenAtRef.current && (userId ? it.user?.id !== userId : true),
        ),
      );
    }
  }, [scope, isLastElementVisible, listRef, questions, sorting.type]);

  useLayoutEffect(() => {
    const [isVisible, scrolledAwayPosition] = isSelectedElementVisible();

    setSelectedQuestionScrolledAway(!isVisible);
    setScrolledAwayNotificationPosition(scrolledAwayPosition);
  }, [isSelectedElementVisible, selectedQuestionElement, sorting]);

  return {
    lastSeenAtRef,
    canShowNewQuestionsNotification,
    canShowScrolledAway,
    scrolledAwayNotificationPosition,
    newQuestionNotificationPosition,
    onScrollInQuestionList,
    onDismissNewQuestionsNotification,
    onConfirmScrollToSelectedQuestion,
    onConfirmShowNewQuestions,
  };
}
